import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useDineTagGroups } from 'hooks/api/useDineOps';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineTagGroup } from 'api/types';
import DineOpsTagGroupModal from 'components/modals/modules/dineops-module/DineOpsTagGroupModal';

/**
 * MENU-006 — tag groups and their tags: the vocabulary the Attributes tab is
 * built from.
 *
 * Nothing on the item dialog's Attributes tab is hand-coded; it renders what
 * this master holds. Add a group here and every dish can answer it. The one
 * group that ships, Food type, is BOUND to the is_veg column and cannot be
 * deleted; everything else is the restaurant's to define (decided 2026-10-01).
 */

interface Filters {
  selectMode?: string;
  inherited?: string;
  includeInactive?: string;
}

const DineOpsTagsScreen: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [confirmDelete, setConfirmDelete] = useState<DineTagGroup | null>(null);
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; record: DineTagGroup | null } | null>(null);

  const query = useMemo(
    () => ({
      page: 1,
      pageSize: 200,
      searchTerm: searchQuery || undefined,
      includeInactive: filters.includeInactive === 'true' || undefined,
    }),
    [searchQuery, filters.includeInactive],
  );

  const { data: groups, loading, error, refetch } = useDineTagGroups(query);

  // Mode and inheritance are columns already in hand; filtered here, not on the server.
  const rows = useMemo(
    () => groups.filter((g) =>
      (!filters.selectMode || g.selectMode === filters.selectMode)
      && (!filters.inherited || String(g.isInherited) === filters.inherited)),
    [groups, filters.selectMode, filters.inherited],
  );

  const handleRefresh = useCallback(() => void refetch(), [refetch]);
  const handleAdd = useCallback(() => setEditing({ mode: 'create', record: null }), []);

  const handleDelete = useCallback(async () => {
    if (!confirmDelete) return;
    try {
      await dineOpsService.deleteTagGroup(confirmDelete.id, confirmDelete.rowVersion);
      notificationService.success('Removed', `'${confirmDelete.tagGroupName}' deleted.`);
      setConfirmDelete(null);
      void refetch();
    } catch (err: any) {
      // 409 for a bound group; the API says why, so pass its words through.
      notificationService.error('Not deleted', err?.response?.data?.message ?? 'The tag group could not be deleted.');
      setConfirmDelete(null);
    }
  }, [confirmDelete, refetch]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      { id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: handleAdd, tooltip: 'Add a tag group', variant: 'icon' },
      { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: handleRefresh, tooltip: 'Refresh', variant: 'icon' },
    ],
    [handleAdd, handleRefresh],
  );

  const columns: SyutGridColumn<DineTagGroup>[] = useMemo(
    () => [
      { id: 'tagGroupCode', header: 'Code', accessorKey: 'tagGroupCode', enableSorting: true, size: 130 },
      {
        id: 'tagGroupName',
        header: 'Group',
        accessorKey: 'tagGroupName',
        enableSorting: true,
        size: 200,
        cell: ({ row }) => (
          <span>
            {row.original.tagGroupName}
            {row.original.isDanger && <span className="text-danger" style={{ marginLeft: 6, fontSize: 11 }}>safety</span>}
          </span>
        ),
      },
      {
        id: 'tags',
        header: 'Tags',
        accessorKey: 'tags',
        enableSorting: false,
        size: 320,
        // The words themselves: a count says nothing about whether the group is complete.
        cell: ({ row }) => {
          const list = row.original.tags ?? [];
          if (list.length === 0) return <span style={{ opacity: 0.6 }}>none yet</span>;
          return (
            <span>
              {list.slice(0, 6).map((t) => t.tagName).join(', ')}
              {list.length > 6 ? ` +${list.length - 6} more` : ''}
            </span>
          );
        },
      },
      {
        id: 'selectMode',
        header: 'Answers',
        accessorKey: 'selectMode',
        enableSorting: true,
        size: 90,
        cell: ({ row }) => (row.original.selectMode === 'MANY' ? 'Many' : 'One'),
      },
      {
        id: 'isInherited',
        header: 'Inherited',
        accessorKey: 'isInherited',
        enableSorting: true,
        size: 100,
        cell: ({ row }) => (row.original.isInherited ? 'Yes' : <span style={{ opacity: 0.6 }}>per dish</span>),
      },
      {
        id: 'bindsTo',
        header: 'Writes',
        accessorKey: 'bindsTo',
        enableSorting: false,
        size: 110,
        cell: ({ row }) => (row.original.bindsTo ? <code title="Bound: this answer writes a dish column">{row.original.bindsTo}</code> : <span style={{ opacity: 0.6 }}>—</span>),
      },
      { id: 'displayOrder', header: 'Order', accessorKey: 'displayOrder', enableSorting: true, size: 80 },
      {
        id: 'isActive',
        header: 'Status',
        accessorKey: 'isActive',
        enableSorting: true,
        size: 90,
        cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive'),
      },
    ],
    [],
  );

  const getRowActions = useCallback(
    (row: DineTagGroup) => [
      { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => setEditing({ mode: 'edit', record: row }) },
      {
        id: 'delete',
        label: row.bindsTo ? 'Delete (bound — refused)' : 'Delete',
        icon: <IconSystem.trash size={16} />,
        onClick: () => setConfirmDelete(row),
        variant: 'danger' as const,
      },
    ],
    [],
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Tags"
        description="Tag groups and tags — the words a dish is described with on the Attributes tab"
        icon={<IconSystem.tag size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search tag groups' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={rows}
              columns={columns}
              enableSorting
              enablePagination
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={
                error
                  ? 'The tag groups could not be loaded.'
                  : 'No tag groups match. Add one with + above, or clear the filters.'
              }
            />
          </div>

          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Answers"
                  options={[{ value: '', label: 'Any' }, { value: 'ONE', label: 'One' }, { value: 'MANY', label: 'Many' }]}
                  value={filters.selectMode || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, selectMode: (v as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Inherited"
                  options={[{ value: '', label: 'Any' }, { value: 'true', label: 'Inherited from the menu group' }, { value: 'false', label: 'Per dish' }]}
                  value={filters.inherited || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, inherited: (v as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Include inactive"
                  options={[{ value: '', label: 'Active only' }, { value: 'true', label: 'Include inactive' }]}
                  value={filters.includeInactive || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, includeInactive: (v as string) || undefined }))}
                  placeholder="Active only"
                  size="sm"
                  isClearable
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <DineOpsTagGroupModal
          isOpen
          mode={editing.mode}
          record={editing.record}
          onSave={() => void refetch()}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          open
          title="Delete this tag group?"
          message={
            confirmDelete.bindsTo
              ? `'${confirmDelete.tagGroupName}' writes the ${confirmDelete.bindsTo} column on every dish. It cannot be deleted; the API will refuse.`
              : `'${confirmDelete.tagGroupName}' and its ${confirmDelete.tagCount} tag(s) will be removed. Answers dishes gave to it disappear from the Attributes tab.`
          }
          confirmLabel="Delete"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default DineOpsTagsScreen;
